/*
 * Plan Builder local state — persists answers (op/exp/gap/miracle) at
 * `motr_plan_v1`. Lens lives separately at `motr-lens-v1` (managed by
 * `@shared/lens`) so the Workbench can share it.
 *
 * Tolerant load: malformed JSON or missing keys never throws. Save is
 * a fire-and-forget — quota errors are swallowed (the user can still
 * complete the diagnostic; they just lose persistence).
 */
import type {
  ContactInfo,
  DiagnosticAnswers,
  OrgProfile,
  PlanBuilderLocalState,
  PrimaryBlocker,
  PremortemReason,
} from './types';

const STORAGE_KEY = 'motr_plan_v1';

export function loadLocalState(): PlanBuilderLocalState {
  const empty: PlanBuilderLocalState = { version: 1, answers: {} };
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return empty;
  }
  if (!raw) return empty;
  try {
    const parsed = JSON.parse(raw) as Partial<PlanBuilderLocalState> & {
      // Legacy prototype shape: flat answers object at the top level.
      // We tolerate it for buyers who used the prototype before.
      [k: string]: unknown;
    };

    // If there's a nested `answers` object, prefer it; otherwise treat
    // the parsed payload itself as the legacy flat shape.
    const source: Record<string, unknown> =
      parsed && typeof parsed === 'object' && parsed.answers && typeof parsed.answers === 'object'
        ? (parsed.answers as Record<string, unknown>)
        : (parsed as Record<string, unknown>);

    const answers: DiagnosticAnswers = {};
    const intKeys = ['op1', 'op2', 'op3', 'op4', 'op5'] as const;
    intKeys.forEach((k) => {
      const v = source[k];
      if (typeof v === 'number' && v >= 1 && v <= 5) {
        answers[k] = v as DiagnosticAnswers[typeof k];
      }
    });
    const expKeys = ['exp1', 'exp2', 'exp3', 'exp4', 'exp5', 'exp6'] as const;
    expKeys.forEach((k) => {
      const v = source[k];
      if (typeof v === 'number' && v >= 1 && v <= 4) {
        answers[k] = v as DiagnosticAnswers[typeof k];
      }
    });
    const miracleKeys = ['miracle_desk', 'miracle_midday', 'miracle_gc'] as const;
    miracleKeys.forEach((k) => {
      const v = source[k];
      if (typeof v === 'string' && v.trim()) answers[k] = v;
    });
    if (typeof source.gap1 === 'number' && source.gap1 >= 1 && source.gap1 <= 5) {
      answers.gap1 = source.gap1 as DiagnosticAnswers['gap1'];
    }
    const blockerValues: PrimaryBlocker[] = [
      'workload',
      'process',
      'visibility',
      'tools',
      'culture',
      'clarity',
    ];
    if (typeof source.gap2 === 'string' && (blockerValues as string[]).includes(source.gap2)) {
      answers.gap2 = source.gap2 as PrimaryBlocker;
    }
    const reasonValues: PremortemReason[] = [
      'leadership',
      'capacity',
      'buyin',
      'measurement',
      'scope',
      'external',
    ];
    if (typeof source.gap3 === 'string' && (reasonValues as string[]).includes(source.gap3)) {
      answers.gap3 = source.gap3 as PremortemReason;
    }
    return {
      version: 1,
      answers,
      submittedAt:
        typeof parsed.submittedAt === 'string' ? parsed.submittedAt : undefined,
    };
  } catch {
    return empty;
  }
}

export function saveLocalState(state: PlanBuilderLocalState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota or private mode — diagnostic still works in-memory */
  }
}

export function clearLocalState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* same fallback as save */
  }
}

const CONTACT_KEY = 'motr_plan_contact_v1';

export function saveContactDraft(c: Partial<ContactInfo & OrgProfile>): void {
  try { localStorage.setItem(CONTACT_KEY, JSON.stringify(c)); } catch {}
}

export function loadContactDraft(): Partial<ContactInfo & OrgProfile> {
  try {
    const raw = localStorage.getItem(CONTACT_KEY);
    return raw ? (JSON.parse(raw) as Partial<ContactInfo & OrgProfile>) : {};
  } catch {
    return {};
  }
}

export function clearContactDraft(): void {
  try { localStorage.removeItem(CONTACT_KEY); } catch {}
}
